import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { aspirantSignupSchema, type AspirantSignupInput, type AspirantSignupResponse } from '@udbodhanam/shared';
import apiClient from '../services/apiClient';
import ThemeToggle from '../components/UI/ThemeToggle';
import LanguageSelector from '../components/UI/LanguageSelector';
import RazorpayCheckout from '../components/payment/RazorpayCheckout';
import { PAYMENT_RECOVERY_KEY } from '../lib/razorpay';
import PlanConfirmation from '../components/payment/PlanConfirmation';
import { ModalShell } from '../components/UI/ModalShell';

export default function AspirantSignup() {
  const { t } = useTranslation('auth');
  const [checkoutData, setCheckoutData] = useState<AspirantSignupResponse | null>(null);
  const [isRecovery, setIsRecovery] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Restore an in-progress checkout after a page refresh
  useEffect(() => {
    const stored = sessionStorage.getItem(PAYMENT_RECOVERY_KEY);
    if (!stored) return;
    try {
      const data = JSON.parse(stored) as AspirantSignupResponse;
      if (data.payments_id) {
        setIsRecovery(true);
        setCheckoutData(data);
      }
    } catch {
      sessionStorage.removeItem(PAYMENT_RECOVERY_KEY);
    }
  }, []);

  const [showConsentModal, setShowConsentModal] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AspirantSignupInput>({
    resolver: zodResolver(aspirantSignupSchema),
    defaultValues: { consent_accepted: false },
  });

  const consentAccepted = watch('consent_accepted');

  function handleConsentAgree() {
    setValue('consent_accepted', true, { shouldValidate: true });
    setShowConsentModal(false);
  }

  async function onSubmit(values: AspirantSignupInput) {
    setServerError('');
    try {
      const res = await apiClient.post<{ data: AspirantSignupResponse }>(
        '/aspirant/signup',
        values,
      );
      setCheckoutData(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setServerError(t('signup.errorDuplicate'));
      } else {
        setServerError(t('signup.errorServer'));
      }
    }
  }

  if (checkoutData && isRecovery) {
    return <RazorpayCheckout checkoutData={checkoutData} skipModal={true} />;
  }

  if (checkoutData && !confirmed) {
    return <PlanConfirmation checkoutData={checkoutData} onProceed={() => setConfirmed(true)} />;
  }

  if (checkoutData && confirmed) {
    return <RazorpayCheckout checkoutData={checkoutData} />;
  }

  return (
    <main className="flex min-h-screen">
      {/* LEFT — hero panel (hidden on mobile) */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -80, left: -80, width: 320, height: 320, background: 'rgba(255,255,255,0.07)' }}
        />
        <h1 className="font-display text-5xl font-normal mb-4 relative z-10">
          {t('login.benefit1')}
        </h1>
        <ul className="space-y-3 relative z-10 text-white/90 text-base">
          <li>✦ {t('login.benefit2')}</li>
          <li>✦ {t('login.benefit3')}</li>
        </ul>
      </div>

      {/* RIGHT — signup form */}
      <div className="flex flex-1 flex-col min-h-screen bg-surface">
        {/* Top bar */}
        <div className="flex items-center justify-end gap-2 px-6 py-3">
          <LanguageSelector />
          <ThemeToggle />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-10">
          <div className="w-full max-w-lg">
            <h2 className="text-2xl font-semibold text-earth-800 mb-1">{t('signup.title')}</h2>
            <p className="text-sm text-text-muted mb-6">{t('signup.subtitle')}</p>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              {/* Full name */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.fullNameLabel')}<span className="ml-0.5">*</span>
                </label>
                <input
                  {...register('full_name')}
                  type="text"
                  placeholder={t('signup.fullNamePlaceholder')}
                  className="w-full h-10 rounded-xl border border-border bg-surface px-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                {errors.full_name && (
                  <p className="mt-1 text-xs text-error">{errors.full_name.message}</p>
                )}
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.usernameLabel')}<span className="ml-0.5">*</span>
                </label>
                <input
                  {...register('username')}
                  type="text"
                  placeholder={t('signup.usernamePlaceholder')}
                  autoComplete="username"
                  className="w-full h-10 rounded-xl border border-border bg-surface px-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                {errors.username && (
                  <p className="mt-1 text-xs text-error">{errors.username.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.emailLabel')}<span className="ml-0.5">*</span>
                </label>
                <input
                  {...register('email')}
                  type="email"
                  placeholder={t('signup.emailPlaceholder')}
                  autoComplete="email"
                  className="w-full h-10 rounded-xl border border-border bg-surface px-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-error">{errors.email.message}</p>
                )}
              </div>

              {/* Phone (optional) */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.phoneLabel')}{' '}
                  <span className="text-text-muted font-normal">({t('signup.phoneOptional')})</span>
                </label>
                <input
                  {...register('phone')}
                  type="tel"
                  placeholder={t('signup.phonePlaceholder')}
                  autoComplete="tel"
                  className="w-full h-10 rounded-xl border border-border bg-surface px-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>

              {/* Date of birth */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.dobLabel')}<span className="ml-0.5">*</span>
                </label>
                <input
                  {...register('date_of_birth')}
                  type="date"
                  className="w-full h-10 rounded-xl border border-border bg-surface px-3 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-xs text-error">{errors.date_of_birth.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.passwordLabel')}<span className="ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full h-10 rounded-xl border border-border bg-surface px-3 pr-10 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-earth-400 hover:text-earth-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-error">{errors.password.message}</p>
                )}
                <ul className="mt-2 space-y-0.5 text-xs text-text-muted">
                  <li>{t('passwordRequirements.reqMinLength')}</li>
                  <li>{t('passwordRequirements.reqUppercase')}</li>
                  <li>{t('passwordRequirements.reqLowercase')}</li>
                  <li>{t('passwordRequirements.reqNumber')}</li>
                  <li>{t('passwordRequirements.reqSpecial')}</li>
                </ul>
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  {t('signup.confirmPasswordLabel')}<span className="ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('confirm_password')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full h-10 rounded-xl border border-border bg-surface px-3 pr-10 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-earth-400 hover:text-earth-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="mt-1 text-xs text-error">{errors.confirm_password.message}</p>
                )}
              </div>

              {/* Consent — 2026-09-15: real, affirmative, informed consent.
                  The indicator can only ever flip true via handleConsentAgree()
                  inside the modal (see below) — there is no independent
                  checkbox toggle, so it's not possible to "agree" without
                  actually opening and reading the notice (matches DPDP's
                  affirmative-action requirement + standard clickwrap pattern). */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowConsentModal(true)}
                  className="w-full flex items-start gap-3 text-left rounded-xl border border-border px-3 py-2.5 hover:bg-earth-50 transition-colors"
                >
                  <span
                    className={`mt-0.5 w-[18px] h-[18px] rounded border-2 inline-grid place-items-center flex-shrink-0 ${
                      consentAccepted ? 'border-primary-500 bg-primary-500 text-white' : 'border-earth-300 bg-surface'
                    }`}
                  >
                    {consentAccepted && <span className="text-[12px] font-bold leading-none">✓</span>}
                  </span>
                  <span className="text-sm text-earth-700">
                    {consentAccepted ? t('signup.consentAgreedLabel') : t('signup.consentPromptLabel')}{' '}
                    <span className="text-primary-600 font-medium underline">{t('signup.consentLinkText')}</span><span className="ml-0.5">*</span>
                  </span>
                </button>
                {errors.consent_accepted && (
                  <p className="mt-1 text-xs text-error">{t('signup.consentRequired')}</p>
                )}
              </div>

              {/* Server error */}
              {serverError && (
                <p className="text-sm text-error bg-error-50 rounded-xl px-3 py-2">{serverError}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-10 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors"
              >
                {isSubmitting ? t('signup.submitting') : t('signup.submit')}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-text-muted">
              {t('signup.signinLink')}{' '}
              <Link to="/login" className="text-primary-600 font-medium hover:underline">
                {t('signup.signinLinkAction')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {showConsentModal && (
        <ConsentNoticeModal onAgree={handleConsentAgree} onClose={() => setShowConsentModal(false)} />
      )}
    </main>
  );
}

function ConsentNoticeModal({ onAgree, onClose }: { onAgree: () => void; onClose: () => void }) {
  const { t } = useTranslation('auth');

  return (
    <ModalShell onClose={onClose} closeOnBackdrop={false} className="max-w-xl max-h-[85vh] flex flex-col">
      <div className="p-5 border-b border-border shrink-0">
        <h2 className="text-lg font-semibold text-earth-800">{t('consent.title')}</h2>
        <p className="mt-1 text-sm text-earth-600">{t('consent.intro')}</p>
      </div>

      <div className="p-5 overflow-y-auto space-y-4 text-sm text-earth-700">
        <div className="bg-viveka-50 border border-viveka-200 rounded-lg p-3 text-viveka-800 text-sm flex items-start gap-2">
          <span className="shrink-0 mt-0.5">🔒</span>
          <span>{t('consent.dpdpCallout')}</span>
        </div>
        <section>
          <h3 className="font-semibold text-earth-800 mb-1">{t('consent.dataCollected')}</h3>
          <p>{t('consent.dataCollectedBody')}</p>
        </section>
        <section>
          <h3 className="font-semibold text-earth-800 mb-1">{t('consent.dataUsed')}</h3>
          <p>{t('consent.dataUsedBody')}</p>
        </section>
        <section>
          <h3 className="font-semibold text-earth-800 mb-1">{t('consent.yourRights')}</h3>
          <p>{t('consent.yourRightsBody')}</p>
        </section>
        <section>
          <h3 className="font-semibold text-earth-800 mb-1">{t('consent.retention')}</h3>
          <p>{t('consent.retentionBody')}</p>
        </section>
        <section>
          <h3 className="font-semibold text-earth-800 mb-1">{t('consent.contact')}</h3>
          <p>privacy@udbodhanam.in</p>
        </section>
      </div>

      <div className="p-5 border-t border-border shrink-0 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="h-10 px-4 rounded-xl border border-border text-earth-600 text-sm font-medium hover:bg-earth-50"
        >
          {t('signup.consentCancel')}
        </button>
        <button
          type="button"
          onClick={onAgree}
          className="h-10 px-4 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
        >
          {t('signup.consentAgreeButton')}
        </button>
      </div>
    </ModalShell>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
